import { isEmpty, set } from 'lodash'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { findAllUsersVariables } from '__generatedTypes__/findAllUsers'
import { PlatformName, RolesEnum, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import {
  findAllUsersAccountSharing,
  findAllUsersAccountSharingVariables,
} from '__generatedTypes__/findAllUsersAccountSharing'
import { GET_USERS_ACCOUNT_SHARING } from 'queries/findAllUsersAccountSharing'
import { asUTC } from 'utils/asUtc'

type Filters = {
  selectedPlatform: {
    id: string
    title: string
  }
  role?: RolesEnum
  dateFrom?: Date | null
  dateTo?: Date | null
  isBlocked?: boolean
  membershipDaysRemaining?: Date | null
}

export function castParams({
  page,
  take,
  search,
  filters,
  order,
}: Params<Filters>): findAllUsersAccountSharingVariables {
  const where: findAllUsersAccountSharingVariables['query']['where'] = {}
  const queryFilters: findAllUsersVariables['query']['filters'] = {}

  if (filters.selectedPlatform) {
    where.selectedPlatform = filters.selectedPlatform.id as PlatformName
  }

  if (filters.role) {
    where.role = filters.role
  }

  if (filters.isBlocked) {
    where.isBlocked = filters.isBlocked
  }

  if (filters.dateFrom || filters.dateTo) {
    set(queryFilters, 'createdAt', {
      ...(filters.dateFrom ? { from: filters.dateFrom } : {}),
      ...(filters.dateTo ? { to: filters.dateTo } : {}),
    })
  }

  if (filters.membershipDaysRemaining != null) {
    const targetDate = new Date(filters.membershipDaysRemaining)
    set(where, 'expiresAt', asUTC(targetDate))
  }

  return {
    query: {
      search: {
        query: search,
        type: SearchTypeEnum.ILike,
      },
      skip: (page - 1) * take,
      take,
      filters: queryFilters,
      where: !isEmpty(where) ? where : undefined,
      order: order?.field ? { [order.field]: order.direction } : undefined,
    },
  }
}

export function useAccountSharing() {
  return useQueryWithParams<
    findAllUsersAccountSharing,
    findAllUsersAccountSharingVariables,
    'findAllUsersAccountSharing',
    'items',
    'count',
    Filters
  >(GET_USERS_ACCOUNT_SHARING, 'findAllUsersAccountSharing', 'items', 'count', castParams, undefined, {
    fetchPolicy: 'network-only',
  })
}
